// src/components/home/FaqList.jsx
//
// The FAQ accordion (home-packfinder): `--surface` cards, one open at a time. Used on
// the home page and on /faq with the same items (resolveFaq), so the two never differ.
//
// The answers stay in the DOM when collapsed (the `hidden` attribute), which keeps the
// text findable and the panel ids stable for aria-controls. Questions and answers are
// plain text from Site Text and are rendered as text -- never as HTML.
//
// Standard disclosure pattern: each question is a real <button> inside a heading, with
// aria-expanded + aria-controls; the panel is a labelled region.
import React, { useState } from 'react';

const CARD = 'rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)]';

function Chevron({ open }) {
    return (
        <svg viewBox="0 0 20 20" className={'w-5 h-5 flex-shrink-0 text-book-muted transition-transform ' + (open ? 'rotate-180' : '')}
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 7.5l5 5 5-5" />
        </svg>
    );
}

// `headingLevel` lets the two call sites keep a correct outline: h3 under the home
// page's h2 section heading, h2 under the /faq page's h1.
export default function FaqList({ items, idPrefix = 'faq', headingLevel = 3 }) {
    const [open, setOpen] = useState(null);
    const list = Array.isArray(items) ? items : [];
    if (!list.length) return null;
    const Heading = 'h' + (headingLevel === 2 ? 2 : 3);

    return (
        <div className="space-y-3">
            {list.map((it, i) => {
                const isOpen = open === i;
                const btnId = idPrefix + '-q-' + i;
                const panelId = idPrefix + '-a-' + i;
                return (
                    <div key={i} className={CARD}>
                        <Heading className="m-0">
                            <button type="button" id={btnId} aria-expanded={isOpen} aria-controls={panelId}
                                onClick={() => setOpen(isOpen ? null : i)}
                                className="w-full min-h-[48px] flex items-center justify-between gap-4 text-left px-5 py-4 font-display text-book-text text-base font-semibold leading-snug rounded-[var(--radius-card)]">
                                <span className="min-w-0 [overflow-wrap:anywhere]">{it.q}</span>
                                <Chevron open={isOpen} />
                            </button>
                        </Heading>
                        <div id={panelId} role="region" aria-labelledby={btnId} hidden={!isOpen} className="px-5 pb-5 -mt-1">
                            <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-line max-w-3xl">{it.a}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
